import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { conversationsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Inbox, MessageCircle, CheckCircle2, X, Pencil, Sparkles, Lock,
  Loader2, Send, ArrowLeft, ShieldOff, ShieldAlert,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { relativeTime, messageDisplayTimestamp } from "@/lib/format";
import { ChannelBadge } from "@/components/common/ChannelIcon";
import { MessageBody, MessageAttachments, emailGalleryAttachments } from "@/components/inbox/MessageContent";
import { InboundEmailMessage } from "@/components/inbox/InboundEmailMessage";
import { isEmailChannelMessage, isEmailInboxChannel, messageBubbleClasses, messageMetaClasses, threadMessageRowClass, THREAD_BUBBLE_WIDTH_CLASS } from "@/lib/inboxHelpers";
import { MessageBubbleFooter } from "@/components/inbox/MessageBubbleFooter";
import { InboxChannelEmptyState } from "@/components/inbox/InboxChannelEmptyState";
import { CollapsibleComposerShell } from "@/components/inbox/CollapsibleComposerShell";
import { channelEmptyStateModel } from "@/lib/inboxChannelEmptyModel";
import { countDraftCharacters, resolveReplyComposerContent } from "@/lib/emailComposer";
import {
  inboxConversationPropType,
  inboxMessagePropType,
  inboxSummaryPropType,
} from "@/lib/propTypes/inbox";

function normalizeConversationStatus(status) {
  const s = (status || "open").toLowerCase();
  if (s === "closed" || s === "resolved") return "closed";
  return "open";
}

export function StatusPill({ status }) {
  const normalized = normalizeConversationStatus(status);
  const map = {
    open: ["bg-emerald-50 text-emerald-700 ring-emerald-200", "Open"],
    closed: ["bg-slate-100 text-slate-600 ring-slate-200", "Closed"],
  };
  const [cls, label] = map[normalized];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ring-1",
        cls,
      )}
      data-testid={`status-pill-${normalized}`}
    >
      {label}
    </span>
  );
}
StatusPill.propTypes = { status: PropTypes.string.isRequired };

const CONVERSATION_STATUSES = [
  { id: "open", label: "Open" },
  { id: "closed", label: "Closed" },
];

function ConversationStatusSelect({ status, disabled, onChange }) {
  return (
    <Select value={status} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger
        className="h-7 w-[108px] text-[11px] font-semibold border-slate-200"
        data-testid="conversation-status-select"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {CONVERSATION_STATUSES.map((s) => (
          <SelectItem key={s.id} value={s.id} className="text-[12px]">
            {s.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
ConversationStatusSelect.propTypes = {
  status: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

export function ConversationStatusActions({
  status, disabled, busy, onStatusChange, canUpdateStatus,
}) {
  const blockedTitle = !canUpdateStatus
    ? "Only members and above can update conversation status"
    : undefined;
  const normalizedStatus = normalizeConversationStatus(status);

  return (
    <div className="flex items-center gap-1.5 shrink-0" title={blockedTitle}>
      <ConversationStatusSelect
        status={normalizedStatus}
        disabled={disabled || busy}
        onChange={onStatusChange}
      />
      {!canUpdateStatus && (
        <span className="inline-flex items-center text-amber-600" title={blockedTitle}>
          <Lock size={12} />
        </span>
      )}
    </div>
  );
}
ConversationStatusActions.propTypes = {
  status: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  busy: PropTypes.bool,
  onStatusChange: PropTypes.func.isRequired,
  canUpdateStatus: PropTypes.bool,
};

export function ConversationListItem({ conv, active, onClick }) {
  return (
    <li className="list-none">
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "true" : undefined}
      className={cn(
        "relative w-full text-left px-3 py-3 sm:py-2.5 cursor-pointer transition-colors min-h-14 sm:min-h-0",
        active ? "bg-indigo-50/60" : "hover:bg-slate-50",
      )}
      data-testid={`conv-row-${conv.id}`}
      data-active={active ? "true" : "false"}
    >
      {active && (
        <span
          className="pointer-events-none absolute inset-y-0 left-0 w-[3px] bg-indigo-500"
          data-testid="conv-selection-indicator"
          aria-hidden
        />
      )}
      <div className="flex items-start gap-2.5 min-w-0">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 text-white text-[11.5px] font-medium shrink-0 relative overflow-visible">
          {(conv.contact_name || "??").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
          {conv.is_unread && (
            <span
              className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white shadow-sm"
              data-testid="unread-indicator"
              title="Unread"
              aria-label="Unread"
            />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <div className={cn(
              "text-[12.5px] truncate min-w-0",
              conv.is_unread ? "font-semibold text-slate-900" : "font-medium text-slate-800",
            )}>
              {conv.contact_name || "Unknown sender"}
            </div>
            {conv.has_pending_draft && (
              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[9.5px] font-semibold text-indigo-700 shrink-0">
                <Sparkles size={8} /> Draft
              </span>
            )}
            {conv.is_message_request && (
              <span
                className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[9.5px] font-semibold text-amber-800 shrink-0"
                data-testid="message-request-badge"
              >
                Request
              </span>
            )}
            <span className="ml-auto text-[10px] text-slate-400 shrink-0">
              {conv.last_message_at ? relativeTime(conv.last_message_at) : ""}
            </span>
          </div>
          <div className="mt-1">
            <ChannelBadge channel={conv.channel} className="text-[10px] px-1.5 py-0" />
          </div>
          <div className="mt-1 text-[11.5px] text-slate-500 truncate">
            {conv.last_message_preview || conv.subject || "(No message content)"}
          </div>
          <div className="mt-1 flex items-center gap-1.5">
            <StatusPill status={conv.status} />
            <span className="text-[10px] text-slate-400">
              {conv.message_count} msg{conv.message_count === 1 ? "" : "s"}
            </span>
          </div>
        </div>
      </div>
    </button>
    </li>
  );
}
ConversationListItem.propTypes = {
  conv: inboxConversationPropType.isRequired,
  active: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

function formatDispatchError(code) {
  if (!code) return "Delivery failed";
  if (code === "openclaw_send_timeout") {
    return "OpenClaw did not confirm delivery in time. Retrying may work once the gateway is healthy.";
  }
  if (code === "gmail_not_configured") {
    return "Gmail is not connected for this workspace.";
  }
  if (code === "gmail_reply_target_missing") {
    return "Could not determine who to reply to in this Gmail thread.";
  }
  if (code === "live_outbound_disabled" || String(code).includes("LIVE_OUTBOUND")) {
    return "Live outbound is disabled on this server (SELLIX_LIVE_OUTBOUND). Ask an admin to enable it, then retry.";
  }
  return String(code).replace(/_/g, " ");
}

export function MessageBubble({ msg, assistantName, canRetry, onRetry, retryBusy }) {
  const isInbound = msg.direction === "inbound";
  const isDraft = msg.status === "draft";
  const isSending = msg.status === "sending";
  const isFailed = msg.status === "failed";
  const isDelivered = msg.status === "delivered";
  const isSent = msg.status === "sent";
  const metadata = msg.metadata || {};
  const dispatchMeta = metadata.outbound_dispatch || {};
  const dispatchError = formatDispatchError(dispatchMeta.error || dispatchMeta.reason);
  const gallery = emailGalleryAttachments(metadata);
  const displayTime = messageDisplayTimestamp(msg);
  return (
    <div
      className={threadMessageRowClass(isInbound)}
      data-testid={`msg-bubble-${msg.id}`}
    >
      <div className={cn(
        THREAD_BUBBLE_WIDTH_CLASS,
        "rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed overflow-hidden",
        messageBubbleClasses({ isInbound, isDraft, isFailed, isSending }),
      )}>
        <MessageBody text={msg.body_text} metadata={metadata} />
        <MessageAttachments attachments={gallery} />
        {isFailed && dispatchError && (
          <div className="mt-1.5 text-[11px] text-red-700 [overflow-wrap:anywhere]">
            Delivery failed: {dispatchError}
          </div>
        )}
        {isFailed && canRetry && onRetry && (
          <div className="mt-2">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-[11px] border-red-200 text-red-700 hover:bg-red-100"
              disabled={retryBusy}
              onClick={() => onRetry(msg.id)}
              data-testid="outbound-retry-btn"
            >
              {retryBusy ? "Retrying…" : "Retry send"}
            </Button>
          </div>
        )}
        <div className={cn(
          "mt-1 flex items-center gap-1 text-[10px] flex-wrap",
          messageMetaClasses({ isInbound, isDraft, isFailed, isSending }),
        )}>
          <MessageBubbleFooter
            isInbound={isInbound}
            isDraft={isDraft}
            isSending={isSending}
            isFailed={isFailed}
            isDelivered={isDelivered}
            isSent={isSent}
            assistantName={assistantName}
            status={msg.status}
            displayTime={displayTime}
          />
        </div>
      </div>
    </div>
  );
}
MessageBubble.propTypes = {
  msg: inboxMessagePropType.isRequired,
  assistantName: PropTypes.string.isRequired,
  canRetry: PropTypes.bool,
  onRetry: PropTypes.func,
  retryBusy: PropTypes.bool,
};

export function DraftReviewCard({
  draft, canApprove, assistantName, showActions = true, onApprove, onEdit, onDiscard, busy,
  compact = false,
}) {
  if (compact) {
    return (
      <div className="min-w-0 w-full min-h-0 flex flex-col flex-1" data-testid="draft-review-card">
        <div className="flex items-center gap-1.5 mb-1 shrink-0">
          <Sparkles size={12} className="text-indigo-600 shrink-0" />
          <div className="text-[11.5px] font-semibold text-slate-900 truncate">
            {assistantName} drafted a reply — waiting for your review
          </div>
        </div>
        <div
          className="min-h-[60px] flex-1 overflow-y-auto overflow-x-hidden rounded-md border border-indigo-100 bg-indigo-50/30 px-2 py-1 text-[12px] leading-snug text-slate-700 min-w-0 [&_img]:max-h-10"
          data-testid="draft-review-body"
        >
          <MessageBody text={draft.body_text} metadata={draft.metadata} />
          <MessageAttachments attachments={emailGalleryAttachments(draft.metadata)} />
        </div>
        {!canApprove && (
          <div className="mt-1.5 inline-flex items-center gap-1.5 text-[10.5px] text-amber-700">
            <Lock size={10} /> Only admins or owners can approve drafts.
          </div>
        )}
        {showActions && (
          <div className="mt-2">
            <DraftReviewActions
              canApprove={canApprove}
              busy={busy}
              onApprove={onApprove}
              onEdit={onEdit}
              onDiscard={onDiscard}
              inline
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={threadMessageRowClass(false)} data-testid="draft-review-card">
      <div className="w-fit max-w-[75%] min-w-0 rounded-xl bg-indigo-50/40 p-3">
        <div className="flex items-start gap-2">
          <Sparkles size={13} className="text-indigo-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] font-semibold text-slate-900">
              {assistantName} drafted a reply — waiting for your review
            </div>
            <div
              className="mt-1 max-h-[60px] overflow-y-auto overflow-x-hidden text-[12px] leading-snug text-slate-700 min-w-0 [&_img]:max-h-10"
              data-testid="draft-review-body"
            >
              <MessageBody text={draft.body_text} metadata={draft.metadata} />
              <MessageAttachments attachments={emailGalleryAttachments(draft.metadata)} />
            </div>
            {!canApprove && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-[10.5px] text-amber-700">
                <Lock size={10} /> Only admins or owners can approve drafts.
              </div>
            )}
          </div>
        </div>
        {showActions && (
          <div className="mt-2.5">
            <DraftReviewActions
              canApprove={canApprove}
              busy={busy}
              onApprove={onApprove}
              onEdit={onEdit}
              onDiscard={onDiscard}
              inline
            />
          </div>
        )}
      </div>
    </div>
  );
}
DraftReviewCard.propTypes = {
  draft: inboxMessagePropType.isRequired,
  canApprove: PropTypes.bool,
  assistantName: PropTypes.string.isRequired,
  showActions: PropTypes.bool,
  onApprove: PropTypes.func,
  onEdit: PropTypes.func,
  onDiscard: PropTypes.func,
  busy: PropTypes.bool,
  compact: PropTypes.bool,
};

export function DraftReviewActions({
  canApprove, onApprove, onEdit, onDiscard, busy, inline = false,
}) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-end gap-1.5",
        inline ? "" : "px-3 py-1.5 border-t border-slate-200 bg-white shrink-0",
      )}
      data-testid="draft-review-actions"
    >
      <Button
        variant="ghost" size="sm"
        className="h-9 sm:h-7 text-[11px] text-slate-600"
        disabled={!canApprove || busy}
        onClick={onDiscard}
        data-testid="draft-discard-btn"
      >
        <X size={12} /> Reject
      </Button>
      <Button
        variant="outline" size="sm"
        className="h-9 sm:h-7 text-[11px]"
        disabled={!canApprove || busy}
        onClick={onEdit}
        data-testid="draft-edit-btn"
      >
        <Pencil size={12} /> Edit
      </Button>
      <Button
        size="sm"
        className="h-9 sm:h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700"
        disabled={!canApprove || busy}
        onClick={onApprove}
        data-testid="draft-approve-btn"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
        {busy ? "Sending…" : "Approve & send"}
      </Button>
    </div>
  );
}
DraftReviewActions.propTypes = {
  canApprove: PropTypes.bool,
  onApprove: PropTypes.func,
  onEdit: PropTypes.func,
  onDiscard: PropTypes.func,
  busy: PropTypes.bool,
  inline: PropTypes.bool,
};

export function DraftReviewPanel({
  draft,
  drafts = [],
  selectedDraftId = "",
  onSelectDraft,
  canApprove,
  assistantName,
  busy,
  onApprove,
  onEdit,
  onDiscard,
  conversationId = "",
}) {
  let queue = [];
  if (drafts && drafts.length > 0) {
    queue = drafts;
  } else if (draft) {
    queue = [draft];
  }
  const active = queue.find((d) => String(d.id) === String(selectedDraftId || draft?.id))
    || queue[0]
    || draft;
  const { bodyText, bodyHtml } = resolveReplyComposerContent(active);
  const charCount = countDraftCharacters({ bodyText, bodyHtml });
  const storageKey = conversationId
    ? `draft-review:${conversationId}`
    : `draft-review:${active?.id || "default"}`;
  const failedHint = active?.metadata?.provider_error?.reviewer_message
    || (active?.metadata?.draft_status === "failed"
      ? "AI draft generation failed. Edit or regenerate."
      : null);

  return (
    <CollapsibleComposerShell
      title={queue.length > 1 ? `Pending drafts (${queue.length})` : "Draft reply"}
      statusLabel="Draft"
      charCount={charCount}
      storageKey={storageKey}
      forceExpandKey={active?.id || ""}
      defaultExpanded
      defaultHeight={queue.length > 1 ? 200 : 160}
      minHeight={120}
      testId="draft-review-panel"
    >
      <div className="flex flex-col min-h-0 flex-1 h-full">
        {queue.length > 1 && (
          <div
            className="px-3 pt-2 pb-1 flex flex-wrap gap-1.5 shrink-0"
            data-testid="draft-queue"
          >
            {queue.map((d, idx) => {
              const selected = String(d.id) === String(active?.id);
              const failed = d?.metadata?.draft_status === "failed";
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => onSelectDraft?.(d.id)}
                  className={cn(
                    "h-7 px-2 rounded-md text-[11px] border transition-colors",
                    selected
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-400",
                    failed && !selected && "border-amber-300 text-amber-800",
                  )}
                  data-testid={`draft-queue-item-${idx + 1}`}
                >
                  Reply {idx + 1}
                  {failed ? " · failed" : ""}
                </button>
              );
            })}
          </div>
        )}
        {failedHint && (
          <div className="px-3 pb-1 text-[11px] text-amber-800" data-testid="draft-provider-error">
            {failedHint}
          </div>
        )}
        <div className="px-3 pt-0.5 pb-0.5 min-h-0 flex-1 flex flex-col overflow-hidden">
          <DraftReviewCard
            draft={active}
            canApprove={canApprove}
            busy={busy}
            showActions={false}
            assistantName={assistantName}
            compact
          />
        </div>
        <DraftReviewActions
          canApprove={canApprove}
          busy={busy}
          onApprove={onApprove}
          onEdit={onEdit}
          onDiscard={onDiscard}
        />
      </div>
    </CollapsibleComposerShell>
  );
}
DraftReviewPanel.propTypes = {
  draft: inboxMessagePropType.isRequired,
  drafts: PropTypes.arrayOf(inboxMessagePropType),
  selectedDraftId: PropTypes.string,
  onSelectDraft: PropTypes.func,
  canApprove: PropTypes.bool,
  assistantName: PropTypes.string.isRequired,
  busy: PropTypes.bool,
  onApprove: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDiscard: PropTypes.func.isRequired,
  conversationId: PropTypes.string,
};

export function InboxReplyBar({
  canReply,
  assistantName,
  draftBusy = false,
  sendBusy = false,
  onSend,
  onRegenerateDraft,
}) {
  const [value, setValue] = useState("");
  const barBusy = draftBusy || sendBusy;

  const submit = async () => {
    const text = value.trim();
    if (!text || barBusy) return;
    const ok = await onSend(text);
    if (ok !== false) setValue("");
  };

  return (
    <div className="px-3 sm:px-4 py-3 border-t border-slate-200 bg-white shrink-0" data-testid="inbox-reply-bar">
      {!canReply && (
        <div className="mb-2 inline-flex items-center gap-1.5 text-[10.5px] text-amber-700">
          <Lock size={10} /> Only admins or owners can reply in this inbox.
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Button
          variant="outline"
          size="sm"
          className="h-9 sm:h-8 text-[11.5px]"
          disabled={!canReply || barBusy}
          onClick={onRegenerateDraft}
          data-testid="regenerate-draft-btn"
        >
          {draftBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
          Ask {assistantName} to draft
        </Button>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-end gap-2">
        <Textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Write a manual reply…"
          rows={2}
          disabled={!canReply || barBusy}
          className="text-[13px] min-h-[52px] resize-none w-full min-w-0"
          data-testid="manual-reply-input"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <Button
          size="sm"
          className="h-10 sm:h-9 shrink-0 w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700"
          disabled={!canReply || barBusy || !value.trim()}
          onClick={submit}
          data-testid="manual-reply-send"
        >
          {sendBusy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
          Send
        </Button>
      </div>
    </div>
  );
}
InboxReplyBar.propTypes = {
  canReply: PropTypes.bool,
  assistantName: PropTypes.string.isRequired,
  draftBusy: PropTypes.bool,
  sendBusy: PropTypes.bool,
  onSend: PropTypes.func.isRequired,
  onRegenerateDraft: PropTypes.func.isRequired,
};

export function EmptyInboxFiltered({ channel, inboxSummary, hasFilters }) {
  const model = channelEmptyStateModel(channel, inboxSummary, hasFilters);
  return (
    <div className="flex-1 min-h-0 overflow-y-auto" data-testid="inbox-filtered-empty">
      <InboxChannelEmptyState compact {...model} />
    </div>
  );
}
EmptyInboxFiltered.propTypes = {
  channel: PropTypes.string.isRequired,
  inboxSummary: inboxSummaryPropType,
  hasFilters: PropTypes.bool,
};

export function EmptyInbox() {
  return (
    <div className="flex-1 flex items-center justify-center bg-white px-4" data-testid="inbox-empty">
      <div className="text-center max-w-md px-2 sm:px-6">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br from-indigo-50 to-violet-50 flex items-center justify-center ring-1 ring-indigo-100">
          <Inbox size={24} className="text-indigo-600" />
        </div>
        <h2 className="mt-4 text-[16px] sm:text-[17px] font-semibold tracking-tight text-slate-900">
          Your inbox is waiting on its first message
        </h2>
        <p className="text-[13px] text-slate-500 mt-1.5">
          WhatsApp and email conversations appear here once messages are received.
          Complete channel setup under Settings → Connections, then send a test
          WhatsApp message — it should show up within a few seconds.
        </p>
        <Link to="/settings/connections">
          <Button className="mt-4 w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700" data-testid="inbox-empty-connect-channels">
            <MessageCircle size={14} /> Manage connections
          </Button>
        </Link>
      </div>
    </div>
  );
}

function InboxDetailLoading() {
  return (
    <div className="flex-1 flex items-center justify-center">
      <Loader2 size={20} className="animate-spin text-indigo-600" />
    </div>
  );
}

function InboxDetailPickConversation() {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center text-slate-500 text-[12.5px]">Pick a conversation on the left.</div>
    </div>
  );
}

function InboxDetailChannelEmpty({ activeChannel, inboxSummary, hasActiveFilters }) {
  return (
    <InboxChannelEmptyState
      compact={false}
      {...channelEmptyStateModel(activeChannel, inboxSummary, hasActiveFilters)}
    />
  );
}
InboxDetailChannelEmpty.propTypes = {
  activeChannel: PropTypes.string.isRequired,
  inboxSummary: inboxSummaryPropType,
  hasActiveFilters: PropTypes.bool,
};

function InboxDetailHeader({
  activeConv,
  canUpdateStatus,
  busyAction,
  onBack,
  onStatusChange,
  onAcceptRequest,
  canAcceptRequest,
}) {
  const status = activeConv.status || "open";
  const isRequest = !!activeConv.is_message_request
    && String(activeConv.channel || "").toLowerCase() === "instagram";

  const [isBlocked, setIsBlocked] = useState(Boolean(activeConv.is_blocked));
  const [blockingBusy, setBlockingBusy] = useState(false);

  useEffect(() => {
    setIsBlocked(Boolean(activeConv.is_blocked));
    if (activeConv?.id) {
      conversationsApi.getBlockStatus(activeConv.id)
        .then((res) => setIsBlocked(Boolean(res?.blocked)))
        .catch(() => {});
    }
  }, [activeConv?.id, activeConv?.is_blocked]);

  const handleToggleBlock = async () => {
    if (blockingBusy || !activeConv?.id) return;
    setBlockingBusy(true);
    try {
      if (isBlocked) {
        await conversationsApi.unblockContact(activeConv.id);
        setIsBlocked(false);
        toast.success("Contact unblocked successfully.");
      } else {
        await conversationsApi.blockContact(activeConv.id);
        setIsBlocked(true);
        toast.error("Contact blocked. Incoming messages from this contact will be dropped.");
      }
    } catch (err) {
      toast.error(err.message || "Failed to update block status.");
    } finally {
      setBlockingBusy(false);
    }
  };

  return (
    <div className="shrink-0 border-b border-slate-200 bg-white">
      <div className="min-h-12 px-2 sm:px-4 py-2 flex items-center gap-2 sm:gap-3 min-w-0">
        {onBack ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="lg:hidden h-10 w-10 shrink-0"
            onClick={onBack}
            aria-label="Back to conversations"
            data-testid="inbox-back-btn"
          >
            <ArrowLeft size={18} className="text-slate-700" />
          </Button>
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <div className="text-[13px] font-semibold text-slate-900 truncate">
              {activeConv.contact_name || "Unknown"}
            </div>
            <ChannelBadge channel={activeConv.channel} />
            {isRequest ? (
              <span
                className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800"
                data-testid="message-request-badge-detail"
              >
                Request
              </span>
            ) : null}
            <StatusPill status={status} />
            {isBlocked ? (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700 ring-1 ring-rose-200"
                data-testid="blocked-badge-detail"
              >
                <ShieldOff size={10} /> Blocked
              </span>
            ) : null}
          </div>
          <div className="hidden sm:block text-[10.5px] text-slate-400 font-mono truncate">
            thread: {activeConv.external_thread_id || "—"}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            type="button"
            variant={isBlocked ? "destructive" : "outline"}
            size="sm"
            className={cn(
              "h-7 text-[11px] font-medium gap-1 px-2.5",
              isBlocked && "bg-rose-600 hover:bg-rose-700 text-white border-rose-600",
            )}
            onClick={handleToggleBlock}
            disabled={blockingBusy}
            data-testid="block-btn"
          >
            {isBlocked ? <ShieldAlert size={12} /> : <ShieldOff size={12} />}
            {isBlocked ? "Unblock" : "Block"}
          </Button>

          <ConversationStatusActions
            status={status}
            disabled={!canUpdateStatus || busyAction === "status"}
            busy={busyAction === "status"}
            canUpdateStatus={canUpdateStatus}
            onStatusChange={onStatusChange}
          />
        </div>
      </div>

      {isBlocked ? (
        <div
          className="mx-2 sm:mx-4 mb-2 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-2 text-[11px] leading-snug text-rose-950 flex items-center gap-1.5"
          data-testid="blocked-warning-banner"
        >
          <ShieldOff size={14} className="text-rose-600 shrink-0" />
          <span className="truncate">
            <strong className="font-semibold">Contact Blocked:</strong> Incoming messages from this contact will be dropped.
          </span>
        </div>
      ) : null}

      {isRequest ? (
        <div
          className="mx-2 sm:mx-4 mb-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] leading-snug text-amber-950"
          data-testid="instagram-message-request-banner"
        >
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <span>
              <span className="font-semibold">Instagram message request.</span>
              {" "}Replying accepts this request and moves it to General in Instagram.
            </span>
            {canAcceptRequest && onAcceptRequest ? (
              <Button
                type="button"
                size="sm"
                className="h-7 text-[11px] bg-amber-700 hover:bg-amber-800 text-white shrink-0"
                onClick={onAcceptRequest}
                data-testid="accept-message-request-btn"
              >
                Accept request
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
InboxDetailHeader.propTypes = {
  activeConv: inboxConversationPropType.isRequired,
  canUpdateStatus: PropTypes.bool,
  busyAction: PropTypes.string,
  onBack: PropTypes.func,
  onStatusChange: PropTypes.func.isRequired,
  onAcceptRequest: PropTypes.func,
  canAcceptRequest: PropTypes.bool,
};

function InboxDetailSubject({ activeConv }) {
  if (!activeConv.subject || !isEmailInboxChannel(activeConv.channel)) return null;
  return (
    <div
      className="shrink-0 border-b border-slate-200 bg-white px-3 sm:px-4 py-2"
      data-testid="conversation-subject"
    >
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        Subject
      </div>
      <div
        className="mt-0.5 text-[13px] font-medium text-slate-900 [overflow-wrap:anywhere]"
        title={activeConv.subject}
      >
        {activeConv.subject}
      </div>
    </div>
  );
}
InboxDetailSubject.propTypes = {
  activeConv: inboxConversationPropType.isRequired,
};

function InboxDetailMessages({
  visibleMessages,
  threadScrollerRef,
  assistantName,
  canApprove,
  onRetryOutbound,
  retryId,
  conversationChannel,
}) {
  return (
    <div
      ref={threadScrollerRef}
      className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-thin py-3 min-w-0"
      data-testid="thread-scroller"
    >
      {visibleMessages.map((m) => (
        isEmailChannelMessage(m, conversationChannel) ? (
          <InboundEmailMessage
            key={m.id}
            msg={m}
            assistantName={assistantName}
            canRetry={canApprove}
            onRetry={onRetryOutbound}
            retryBusy={retryId === m.id}
          />
        ) : (
          <MessageBubble
            key={m.id}
            msg={m}
            assistantName={assistantName}
            canRetry={canApprove}
            onRetry={onRetryOutbound}
            retryBusy={retryId === m.id}
          />
        )
      ))}
    </div>
  );
}
InboxDetailMessages.propTypes = {
  visibleMessages: PropTypes.arrayOf(inboxMessagePropType).isRequired,
  threadScrollerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  assistantName: PropTypes.string.isRequired,
  canApprove: PropTypes.bool,
  onRetryOutbound: PropTypes.func.isRequired,
  retryId: PropTypes.string,
  conversationChannel: PropTypes.string,
};

function InboxDetailFooter({
  hideFooter,
  draft,
  drafts = [],
  selectedDraftId = "",
  onSelectDraft,
  draftGenerating,
  requiresDraftApproval,
  canApprove,
  busyAction,
  assistantName,
  activeConv,
  onApprove,
  openComposerEdit,
  onDiscard,
  onSendManual,
  onRegenerateDraft,
}) {
  // Include busyAction === "regenerate" so a silent poll clearing draftGenerating
  // mid-request cannot fall through to Ask/Send button spinners.
  const isDrafting = Boolean(
    draftGenerating || busyAction === "regenerate",
  );

  if (hideFooter) return null;

  if (draft && requiresDraftApproval) {
    return (
      <DraftReviewPanel
        draft={draft}
        drafts={drafts}
        selectedDraftId={selectedDraftId}
        onSelectDraft={onSelectDraft}
        canApprove={canApprove && !isDrafting}
        busy={!!busyAction || isDrafting}
        assistantName={assistantName}
        conversationId={activeConv?.id || ""}
        onApprove={onApprove}
        onEdit={openComposerEdit}
        onDiscard={onDiscard}
      />
    );
  }
  if (isDrafting && requiresDraftApproval) {
    return (
      <div
        className="shrink-0 border-t border-slate-200 bg-white px-4 py-3 flex items-center gap-2 text-[13px] text-slate-600"
        data-testid="draft-generating-banner"
       
        aria-live="polite"
      >
        <Loader2 size={14} className="animate-spin text-indigo-600 shrink-0" />
        <span>{assistantName} is drafting a reply…</span>
      </div>
    );
  }
  return (
    <InboxReplyBar
      canReply={canApprove}
      assistantName={assistantName}
      draftBusy={isDrafting}
      sendBusy={busyAction === "send"}
      onSend={onSendManual}
      onRegenerateDraft={onRegenerateDraft}
    />
  );
}
InboxDetailFooter.propTypes = {
  hideFooter: PropTypes.bool,
  draft: inboxMessagePropType,
  drafts: PropTypes.arrayOf(inboxMessagePropType),
  selectedDraftId: PropTypes.string,
  onSelectDraft: PropTypes.func,
  draftGenerating: PropTypes.bool,
  requiresDraftApproval: PropTypes.bool,
  canApprove: PropTypes.bool,
  busyAction: PropTypes.string,
  assistantName: PropTypes.string.isRequired,
  activeConv: inboxConversationPropType,
  onApprove: PropTypes.func.isRequired,
  openComposerEdit: PropTypes.func.isRequired,
  onDiscard: PropTypes.func.isRequired,
  onSendManual: PropTypes.func.isRequired,
  onRegenerateDraft: PropTypes.func.isRequired,
};

export function InboxDetailPanel({
  detailLoading,
  activeConv,
  activeChannel,
  inboxSummary,
  hasActiveFilters,
  listLength,
  threadScrollerRef,
  visibleMessages,
  assistantName,
  canApprove,
  canUpdateStatus,
  busyAction,
  retryId,
  draft,
  drafts = [],
  selectedDraftId = "",
  onSelectDraft,
  draftGenerating = false,
  requiresDraftApproval,
  hideFooter = false,
  onBack,
  onStatusChange,
  onRetryOutbound,
  onApprove,
  onDiscard,
  onRegenerateDraft,
  onSendManual,
  openComposerEdit,
  onAcceptRequest,
}) {
  if (detailLoading) return <InboxDetailLoading />;
  if (activeConv) {
    return (
      <div className="flex flex-1 flex-col min-h-0 min-w-0" data-testid="inbox-thread-view">
        <InboxDetailHeader
          activeConv={activeConv}
          canUpdateStatus={canUpdateStatus}
          busyAction={busyAction}
          onBack={onBack}
          onStatusChange={onStatusChange}
          onAcceptRequest={onAcceptRequest}
          canAcceptRequest={canApprove}
        />
        <InboxDetailSubject activeConv={activeConv} />
        <InboxDetailMessages
          visibleMessages={visibleMessages}
          threadScrollerRef={threadScrollerRef}
          assistantName={assistantName}
          canApprove={canApprove}
          onRetryOutbound={onRetryOutbound}
          retryId={retryId}
          conversationChannel={activeConv.channel}
        />
        <InboxDetailFooter
          hideFooter={hideFooter}
          draft={draft}
          drafts={drafts}
          selectedDraftId={selectedDraftId}
          onSelectDraft={onSelectDraft}
          draftGenerating={draftGenerating}
          requiresDraftApproval={requiresDraftApproval}
          canApprove={canApprove}
          busyAction={busyAction}
          assistantName={assistantName}
          activeConv={activeConv}
          onApprove={onApprove}
          openComposerEdit={openComposerEdit}
          onDiscard={onDiscard}
          onSendManual={onSendManual}
          onRegenerateDraft={onRegenerateDraft}
        />
      </div>
    );
  }
  if (listLength === 0 && activeChannel !== "all") {
    return (
      <InboxDetailChannelEmpty
        activeChannel={activeChannel}
        inboxSummary={inboxSummary}
        hasActiveFilters={hasActiveFilters}
      />
    );
  }
  return <InboxDetailPickConversation />;
}
InboxDetailPanel.propTypes = {
  detailLoading: PropTypes.bool,
  activeConv: inboxConversationPropType,
  activeChannel: PropTypes.string.isRequired,
  inboxSummary: inboxSummaryPropType,
  hasActiveFilters: PropTypes.bool,
  listLength: PropTypes.number.isRequired,
  threadScrollerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  visibleMessages: PropTypes.arrayOf(inboxMessagePropType).isRequired,
  assistantName: PropTypes.string.isRequired,
  canApprove: PropTypes.bool,
  canUpdateStatus: PropTypes.bool,
  busyAction: PropTypes.string,
  retryId: PropTypes.string,
  draft: inboxMessagePropType,
  drafts: PropTypes.arrayOf(inboxMessagePropType),
  selectedDraftId: PropTypes.string,
  onSelectDraft: PropTypes.func,
  draftGenerating: PropTypes.bool,
  requiresDraftApproval: PropTypes.bool,
  hideFooter: PropTypes.bool,
  onBack: PropTypes.func,
  onStatusChange: PropTypes.func.isRequired,
  onRetryOutbound: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
  onDiscard: PropTypes.func.isRequired,
  onRegenerateDraft: PropTypes.func.isRequired,
  onSendManual: PropTypes.func.isRequired,
  openComposerEdit: PropTypes.func.isRequired,
  onAcceptRequest: PropTypes.func,
};
